import { Component, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthService, City } from '../../services/auth.service';
import { AppStateService } from '../../services/app-state.service';
import { DataLayerService } from '../../services/datalayer.service';

@Component({
  selector: 'app-auth-modal',
  templateUrl: './auth-modal.component.html'
})
export class AuthModalComponent implements OnInit {
  @Input() isOpen = false;
  @Output() closeModal = new EventEmitter<void>();

  authMode: 'login' | 'signup' | 'forgot' = 'login';
  loginForm: FormGroup;
  signupForm: FormGroup;
  forgotForm: FormGroup;

  loading = false;
  otpLoading = false;
  otpSent = false;
  errorMsg = '';
  successMsg = '';
  cities: City[] = [];

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private state: AppStateService,
    private dataLayer: DataLayerService,
  ) {
    this.loginForm = this.fb.group({
      phone: ['', [Validators.pattern(/^[0-9]{10}$/)]],
      otp: [''],
      email: ['', [Validators.email]],
      password: ['', [Validators.minLength(6)]]
    });

    this.forgotForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]]
    });

    this.signupForm = this.fb.group({
      fullName: ['', [Validators.required, Validators.minLength(2)]],
      phone: ['', [Validators.required, Validators.pattern(/^[0-9]{10}$/)]],
      otp: ['', [Validators.required, Validators.pattern(/^[0-9]{6}$/)]]
    });
  }

  ngOnInit() {
    this.loadCities();
  }

  @HostListener('document:keydown.escape')
  onEscapeKey() {
    if (this.isOpen) this.close();
  }

  open(mode: 'login' | 'signup' | 'forgot' = 'signup') {
    this.authMode = mode;
    this.loginForm.reset();
    this.signupForm.reset();
    this.forgotForm.reset();
    this.clearMessages();
    this.otpSent = false;
  }

  close() {
    this.clearMessages();
    this.closeModal.emit();
  }

  switchToSignup() {
    this.authMode = 'signup';
    this.signupForm.reset();
    this.clearMessages();
    this.otpSent = false;
  }

  switchToLogin() {
    this.authMode = 'login';
    this.loginForm.reset();
    this.clearMessages();
    this.otpSent = false;
  }

  switchToForgot() {
    this.authMode = 'forgot';
    this.forgotForm.reset();
    this.clearMessages();
  }

  clearMessages() {
    this.errorMsg = '';
    this.successMsg = '';
  }

  /**
   * The reason an auth call failed, whichever backend answered.
   *
   * Auth moved to the new backend, which returns Laravel's validation shape:
   *
   *     { message: "...", errors: { phone: ["No account found for..."] } }
   *
   * The old backend returned `{ msg }` or `{ error }`. Reading only those — as
   * every handler here used to — meant the new shape produced neither, and every
   * specific reason collapsed into a generic fallback: a customer whose OTP had
   * simply expired was told "OTP login failed".
   *
   * `errors` is read FIRST because it is the real per-field reason. `msg` is
   * still honoured after it: the new backend adds that key on these routes
   * precisely so a cached older bundle keeps showing real text, and the old
   * backend's remaining endpoints (resetPassword, city_new) still speak it.
   *
   * `message` is read LAST, because Laravel's summary appends "(and 1 more
   * error)" and reads like a developer note rather than something to show a
   * customer.
   */
  private failureText(err: any, fallback: string): string {
    const errors = err?.error?.errors;
    if (errors && typeof errors === 'object') {
      const first = ([] as string[]).concat(...Object.values(errors) as string[][])[0];
      if (first) return first;
    }

    const legacy = err?.error?.msg || err?.error?.error;
    if (typeof legacy === 'string' && legacy) return legacy;

    // Some legacy endpoints put a field map under `error` rather than a string.
    if (legacy && typeof legacy === 'object') {
      const first = ([] as string[]).concat(...Object.values(legacy) as string[][])[0];
      if (first) return first;
    }

    return err?.error?.message || fallback;
  }

  // --- LOGIN ---
  onLogin() {
    this.clearMessages();
    const { phone, otp, email, password } = this.loginForm.value;

    // OTP login
    if (phone && otp) {
      this.loading = true;
      this.authService.loginWithOtp({ phone, otp, session_id: this.state.sessionId }).subscribe({
        next: (res: any) => {
          this.loading = false;
          this.handleAuthSuccess(res);
        },
        error: (err) => {
          this.loading = false;
          this.errorMsg = this.failureText(err, 'Could not sign you in. Please try again.');
        }
      });
      return;
    }

    // Email/password login
    if (email && password) {
      this.loading = true;
      this.authService.login({ email, password, session_id: this.state.sessionId }).subscribe({
        next: (res: any) => {
          this.loading = false;
          this.handleAuthSuccess(res);
        },
        error: (err) => {
          this.loading = false;
          this.errorMsg = err.error?.error === 'Unauthorized'
            ? 'Invalid email or password'
            : (err.error?.error || 'Login failed');
        }
      });
      return;
    }

    // Neither filled properly. The message names only the OTP path because that
    // is the only one on screen — the email and password inputs are commented out
    // in the template, so telling someone to use them points at fields they
    // cannot see.
    this.errorMsg = 'Enter your phone number and the OTP to sign in.';
    this.loginForm.markAllAsTouched();
  }

  // --- SIGNUP ---
  onSignup() {
    this.clearMessages();
    if (!this.signupForm.valid) {
      this.signupForm.markAllAsTouched();
      return;
    }

    const { fullName, phone, otp } = this.signupForm.value;
    this.loading = true;

    this.authService.register({ name: fullName, phone, otp, session_id: this.state.sessionId }).subscribe({
      next: (res: any) => {
        this.loading = false;
        this.handleAuthSuccess(res);
      },
      error: (err) => {
        this.loading = false;
        this.errorMsg = this.failureText(err, 'Could not create your account. Please try again.');
      }
    });
  }

  // --- FORGOT PASSWORD ---
  onForgotPassword() {
    this.clearMessages();
    if (!this.forgotForm.valid) {
      this.forgotForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.authService.resetPassword(this.forgotForm.value.email).subscribe({
      next: (res: any) => {
        this.loading = false;
        this.successMsg = res.msg || 'Password reset link sent to your email';
      },
      error: (err) => {
        this.loading = false;
        this.errorMsg = this.failureText(err, 'Could not send the reset link. Please try again.');
      }
    });
  }

  // --- SEND OTP ---
  sendOtp(formType: 'login' | 'signup') {
    this.clearMessages();
    const phone = formType === 'login'
      ? this.loginForm.get('phone')?.value
      : this.signupForm.get('phone')?.value;

    if (!phone || !/^[0-9]{10}$/.test(phone)) {
      this.errorMsg = 'Enter a valid 10-digit phone number';
      return;
    }

    const type = formType === 'signup' ? 'register' : 'login';
    this.otpLoading = true;

    this.authService.generateOtp({ mobile_no: phone, type }).subscribe({
      next: (res: any) => {
        this.otpLoading = false;
        this.otpSent = true;
        this.successMsg = res.message || 'OTP sent successfully';
      },
      error: (err) => {
        this.otpLoading = false;
        this.errorMsg = this.failureText(err, 'Could not send the OTP. Please try again.');
      }
    });
  }

  // --- AUTH SUCCESS HANDLER ---
  private handleAuthSuccess(res: any) {
    const user = res.data;
    if (user?.token) {
      this.authService.saveAuth(user);
      // Determine login method used
      const { phone, otp, email } = this.loginForm.value;
      const method = (phone && otp) ? 'otp' : (email ? 'email' : 'otp');
      // Push GTM event
      if (this.authMode === 'signup') {
        this.dataLayer.pushSignUp(user, 'otp');
      } else {
        this.dataLayer.pushLogin(user, method);
      }
      // Reload cart so guest items (now merged to customer by backend) appear immediately
      this.state.loadCartFromApi(true);
      // Merge any guest wishlist items to the server, then load the saved wishlist
      this.state.loadWishlistFromApi();
      this.state.showToast(`Welcome, ${user.name}!`, 'info');
      this.close();
    }
  }

  // --- CITIES ---
  private loadCities() {
    this.authService.getCities().subscribe({
      next: (res: any) => {
        this.cities = res?.data ?? [];
      },
      error: () => {}
    });
  }
}
